/**
 * What the dashboards actually compute.
 *
 * Every function here is pure and takes `now` as an argument, which is the only
 * way "overdue" and "waiting eleven days" are testable at all. The React side
 * fetches, sorts nothing, and decides nothing; it renders what these return.
 *
 * Two rules run through the file. The first is that a row is useless unless it
 * says *which guide* it is about, so every row type carries the id, title, and
 * slug needed to link straight to it. The second is that nothing here
 * re-implements a judgement that already exists elsewhere: the gate decides
 * what counts as missing metadata or an unsourced claim, `internalLinks` from
 * the same module decides what an internal link is, and `readPublishState`
 * decides what a publish report says. A dashboard that disagreed with the panel
 * on the guide's own form would be worse than no dashboard.
 */

import {
    internalLinks,
    type Category,
    type GateIssue,
    type GateResult,
    type Guide,
    type GuideStatus,
    type IsoDate,
    type Person,
    type PublishStage
} from "@zennopay/schema";

import { isUnhealthy, readPublishState } from "../publish/publishState";

/** Enough of a guide to name it and link to it. Every row extends this. */
export interface GuideRef {
    id: string;
    title: string;
    slug: string;
    status: GuideStatus;
}

const refOf = (guide: Guide): GuideRef => ({
    id: guide.id,
    title: guide.title?.trim() || "(untitled)",
    slug: guide.slug ?? "",
    status: guide.status
});

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Milliseconds since the epoch, or `undefined` for anything unparseable. */
export function parseIso(value: IsoDate | undefined): number | undefined {
    if (typeof value !== "string" || value.trim() === "") return undefined;
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? undefined : parsed;
}

/** Whole days between two instants, rounded down and never negative. */
export const daysBetween = (from: number, to: number): number =>
    Math.max(0, Math.floor((to - from) / MS_PER_DAY));

export const STATUSES: GuideStatus[] = [
    "draft", "review", "approved", "published", "archived"
];

/* ------------------------------------------------------- 1. the overview -- */

export interface ContentOverview {
    total: number;
    byStatus: Record<GuideStatus, number>;
    /** Guides carrying a future or past `scheduledPublishAt`. */
    scheduled: number;
    overdueReview: number;
    /** Guides whose last publish attempt was blocked, stale, or degraded. */
    unhealthy: number;
    featured: number;
    withoutHeroImage: number;
    /** Total reading minutes of the published set, i.e. how much is out there. */
    publishedReadingMinutes: number;
    lastPublishedAt?: IsoDate;
}

export function contentOverview(guides: Guide[], now: number): ContentOverview {
    const byStatus = Object.fromEntries(
        STATUSES.map((status) => [status, 0])
    ) as Record<GuideStatus, number>;

    let scheduled = 0;
    let overdueReview = 0;
    let unhealthy = 0;
    let featured = 0;
    let withoutHeroImage = 0;
    let publishedReadingMinutes = 0;
    let lastPublished: number | undefined;
    let lastPublishedAt: IsoDate | undefined;

    for (const guide of guides) {
        if (STATUSES.includes(guide.status)) byStatus[guide.status] += 1;
        if (parseIso(guide.scheduledPublishAt) !== undefined) scheduled += 1;

        const nextReview = parseIso(guide.nextReviewAt);
        if (nextReview !== undefined && nextReview < now) overdueReview += 1;

        const state = readPublishState(guide.publishState);
        if (state && isUnhealthy(state.stage)) unhealthy += 1;

        if (guide.featured) featured += 1;

        if (guide.status === "published") {
            if (!guide.heroImage?.storagePath) withoutHeroImage += 1;
            publishedReadingMinutes += guide.readingTime ?? 0;
            const publishedAt = parseIso(guide.publishedAt);
            if (publishedAt !== undefined && (lastPublished === undefined || publishedAt > lastPublished)) {
                lastPublished = publishedAt;
                lastPublishedAt = guide.publishedAt;
            }
        }
    }

    return {
        total: guides.length,
        byStatus,
        scheduled,
        overdueReview,
        unhealthy,
        featured,
        withoutHeroImage,
        publishedReadingMinutes,
        ...(lastPublishedAt ? { lastPublishedAt } : {})
    };
}

/* ------------------------------------------------- 2. the review pipeline -- */

export interface PipelineRow extends GuideRef {
    /** The role the guide is blocked on, in that role's own words. */
    waitingFor: "a reviewer" | "a publisher";
    waitingSince?: IsoDate;
    waitingDays?: number;
    authorName?: string;
}

/**
 * Work that has been handed on and is waiting for somebody.
 *
 * `approved` is here as well as `review`, because a guide that passed review
 * and was never published is stuck in exactly the same way and is easier to
 * forget: nothing in the CMS was pointing at it, and its author has already
 * moved on. Both are labelled with who they are waiting on, oldest first,
 * because the oldest is the one that has gone wrong.
 */
export function reviewPipeline(
    guides: Guide[],
    authors: Map<string, Person>,
    now: number
): PipelineRow[] {
    return guides
        .filter((guide) => guide.status === "review" || guide.status === "approved")
        .map((guide) => {
            const since = parseIso(guide.updatedAt);
            const authorName = guide.authorId ? authors.get(guide.authorId)?.name : undefined;
            return {
                ...refOf(guide),
                waitingFor: guide.status === "review" ? "a reviewer" : "a publisher",
                ...(guide.updatedAt ? { waitingSince: guide.updatedAt } : {}),
                ...(since !== undefined ? { waitingDays: daysBetween(since, now) } : {}),
                ...(authorName ? { authorName } : {})
            } satisfies PipelineRow;
        })
        .sort((a, b) => (b.waitingDays ?? -1) - (a.waitingDays ?? -1));
}

/* ------------------------------------------------ 3. overdue review queue -- */

export interface OverdueRow extends GuideRef {
    nextReviewAt: IsoDate;
    daysOverdue: number;
    lastReviewedAt?: IsoDate;
    reviewerName?: string;
}

/**
 * Guides whose `nextReviewAt` has passed.
 *
 * Not restricted to published guides. A draft that has sat past its review date
 * is stale content too, and hiding it would mean the queue quietly shrank the
 * moment somebody unpublished the thing they were supposed to re-check.
 */
export function overdueReviews(
    guides: Guide[],
    reviewers: Map<string, Person>,
    now: number
): OverdueRow[] {
    return guides
        .flatMap((guide) => {
            const due = parseIso(guide.nextReviewAt);
            if (due === undefined || due >= now || !guide.nextReviewAt) return [];
            const reviewerName = guide.reviewerId
                ? reviewers.get(guide.reviewerId)?.name
                : undefined;
            return [{
                ...refOf(guide),
                nextReviewAt: guide.nextReviewAt,
                daysOverdue: daysBetween(due, now),
                ...(guide.lastReviewedAt ? { lastReviewedAt: guide.lastReviewedAt } : {}),
                ...(reviewerName ? { reviewerName } : {})
            } satisfies OverdueRow];
        })
        .sort((a, b) => b.daysOverdue - a.daysOverdue);
}

/* --------------------------------- 4. missing metadata and citation gaps -- */

/** One guide and the gate's verdict on it, as the dashboards receive it. */
export interface EvaluatedGuide {
    guide: Guide;
    result: GateResult;
}

/**
 * Which gate issues are a metadata problem and which are an evidence problem.
 *
 * Listed by code rather than matched on a prefix, so a new code added to the
 * gate lands in `other` and shows up unclassified instead of silently
 * disappearing from a dashboard whose whole job is to not miss things.
 */
export const METADATA_CODES = new Set([
    "title.missing", "title.duplicate",
    "slug.missing", "slug.format", "slug.duplicate",
    "primaryQuestion.missing",
    "directAnswer.missing", "directAnswer.length",
    "seoTitle.missing", "seoTitle.long",
    "metaDescription.missing", "metaDescription.long",
    "canonicalUrl.missing",
    "heroImage.alt", "ogImage.alt",
    "publishedAt.missing", "publishedAt.invalid",
    "author.missing"
]);

export const CITATION_CODES = new Set([
    "sources.required",
    "claim.orphaned",
    "claim.unverified",
    "reviewer.required"
]);

export type IssueKind = "metadata" | "citation" | "other";

export const kindOf = (issue: GateIssue): IssueKind => {
    if (METADATA_CODES.has(issue.code)) return "metadata";
    if (CITATION_CODES.has(issue.code)) return "citation";
    return "other";
};

export interface WarningRow extends GuideRef {
    metadata: GateIssue[];
    citations: GateIssue[];
    other: GateIssue[];
    /** Blocking issues among all three buckets. */
    blocking: number;
    /** Errors a migration exemption downgraded. Debt, not absolution. */
    waived: number;
}

/**
 * Every guide the gate has something to say about.
 *
 * `link.broken` is excluded here because it has a dashboard of its own that can
 * name the actual hrefs; listing it in both would make the counts on this one
 * read as metadata problems that no amount of metadata fixes.
 */
export function metadataWarnings(evaluated: EvaluatedGuide[]): WarningRow[] {
    return evaluated
        .flatMap(({ guide, result }) => {
            const relevant = result.issues.filter((issue) => issue.code !== "link.broken");
            if (relevant.length === 0 && result.waived.length === 0) return [];
            return [{
                ...refOf(guide),
                metadata: relevant.filter((issue) => kindOf(issue) === "metadata"),
                citations: relevant.filter((issue) => kindOf(issue) === "citation"),
                other: relevant.filter((issue) => kindOf(issue) === "other"),
                blocking: relevant.filter((issue) => issue.severity === "error").length,
                waived: result.waived.length
            } satisfies WarningRow];
        })
        .sort((a, b) =>
            b.blocking - a.blocking ||
            (b.metadata.length + b.citations.length + b.other.length) -
            (a.metadata.length + a.citations.length + a.other.length));
}

/* ------------------------------------------------- 5. recently published -- */

export interface PublishedRow extends GuideRef {
    publishedAt?: IsoDate;
    daysAgo?: number;
    authorName?: string;
    categoryName?: string;
    publishedBy?: string;
}

export function recentlyPublished(
    guides: Guide[],
    authors: Map<string, Person>,
    categories: Category[],
    now: number,
    limit = 15
): PublishedRow[] {
    const categoryNames = new Map(categories.map((category) => [category.id, category.name]));

    return guides
        .filter((guide) => guide.status === "published")
        .map((guide) => {
            const at = parseIso(guide.publishedAt);
            const authorName = guide.authorId ? authors.get(guide.authorId)?.name : undefined;
            const categoryName = categoryNames.get(guide.categoryId);
            return {
                ...refOf(guide),
                ...(guide.publishedAt ? { publishedAt: guide.publishedAt } : {}),
                ...(at !== undefined ? { daysAgo: daysBetween(at, now) } : {}),
                ...(authorName ? { authorName } : {}),
                ...(categoryName ? { categoryName } : {}),
                ...(guide.publishedBy ? { publishedBy: guide.publishedBy } : {})
            } satisfies PublishedRow;
        })
        /* Undated guides last rather than first: `daysAgo` is undefined for
           them, and sorting undefined as 0 would put them at the top of a list
           whose whole meaning is "most recent". */
        .sort((a, b) => (a.daysAgo ?? Number.MAX_SAFE_INTEGER) - (b.daysAgo ?? Number.MAX_SAFE_INTEGER))
        .slice(0, limit);
}

/* -------------------------------------------------------- 6. categories -- */

export interface CategoryRow {
    id: string;
    name: string;
    slug: string;
    isVisible: boolean;
    total: number;
    published: number;
    drafts: number;
    /** Categories the exporter will omit because they have nothing published. */
    emptyOnTheHub: boolean;
}

export interface CategoryCounts {
    rows: CategoryRow[];
    /**
     * Guides pointing at a category id that does not exist.
     *
     * `buildSnapshot` groups by category, so these do not appear on the hub at
     * all. Silently. Which is why they are counted.
     */
    orphaned: GuideRef[];
}

export function categoryCounts(categories: Category[], guides: Guide[]): CategoryCounts {
    const known = new Set(categories.map((category) => category.id));

    const rows = categories
        .map((category) => {
            const mine = guides.filter((guide) => guide.categoryId === category.id);
            const published = mine.filter((guide) => guide.status === "published").length;
            return {
                id: category.id,
                name: category.name,
                slug: category.slug,
                isVisible: category.isVisible !== false,
                total: mine.length,
                published,
                drafts: mine.filter((guide) => guide.status === "draft").length,
                emptyOnTheHub: published === 0
            } satisfies CategoryRow;
        })
        .sort((a, b) => b.published - a.published || a.name.localeCompare(b.name));

    return {
        rows,
        orphaned: guides
            .filter((guide) => !guide.categoryId || !known.has(guide.categoryId))
            .map(refOf)
    };
}

/* ------------------------------------------------------ 7. broken links -- */

export interface BrokenLinkRow extends GuideRef {
    /** Hrefs in the body that the site does not serve. */
    links: string[];
}

/**
 * Internal links the site does not serve.
 *
 * `internalLinks` is imported from `@zennopay/schema`, which is the same
 * function `evaluatePublishGate` calls to raise `link.broken`, so this report
 * and the gate cannot disagree about what a link is or how it is normalized.
 * What it adds is the href itself: the gate's issue is one sentence per broken
 * link on one guide, and a report needs them gathered.
 */
export function brokenLinkReport(guides: Guide[], knownRoutes: Set<string>): BrokenLinkRow[] {
    return guides
        .flatMap((guide) => {
            const links = internalLinks(guide.body ?? "")
                .filter((link) => !knownRoutes.has(link));
            if (links.length === 0) return [];
            return [{ ...refOf(guide), links }];
        })
        .sort((a, b) => b.links.length - a.links.length);
}

/* ----------------------------------------------------- 8. publish health -- */

export interface PublishHealthRow extends GuideRef {
    stage: PublishStage;
    message: string;
    updatedAt: string;
    actor: string;
    issues: GateIssue[];
}

/** Blocked first, then stale, then degraded: worst news at the top. */
const STAGE_RANK: Record<PublishStage, number> = {
    blocked: 0,
    stale: 1,
    degraded: 2,
    unpublished: 3,
    published: 4
};

/**
 * Guides whose last publish attempt did not fully succeed.
 *
 * The three unhealthy stages are kept apart rather than merged into a count,
 * because they mean different things to the person reading this: `blocked`
 * published nothing, `stale` is serving an old version of a live page, and
 * `degraded` is live but lagging.
 */
export function publishHealth(guides: Guide[]): PublishHealthRow[] {
    return guides
        .flatMap((guide) => {
            const state = readPublishState(guide.publishState);
            if (!state || !isUnhealthy(state.stage)) return [];
            return [{
                ...refOf(guide),
                stage: state.stage,
                message: state.message,
                updatedAt: state.updatedAt,
                actor: state.actor,
                issues: (state.issues ?? []) as GateIssue[]
            } satisfies PublishHealthRow];
        })
        .sort((a, b) =>
            STAGE_RANK[a.stage] - STAGE_RANK[b.stage] ||
            (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));
}
